import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, of, switchMap } from 'rxjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PageReloadService } from 'src/app/services/page-reload/page-reload.service';
import { CONSTANTS } from 'src/app/providers/constants';
import { captureException } from '@sentry/angular';
import { Path, RoutePath } from 'src/app/shared/route';
import { toSentryError } from 'src/app/shared/utils/to-sentry-error/to-sentry-error';

export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const constants = inject(CONSTANTS);
  const pageReload = inject(PageReloadService);

  const validate = () =>
    authService.me().pipe(
      map(() => true),
      catchError((error: HttpErrorResponse) => {
        captureException(toSentryError(error));

        console.error(error);

        if (window.shopify) return of(router.createUrlTree(RoutePath.getPath(Path['auth/error']).fullPath));

        return of(router.createUrlTree(RoutePath.getPath(Path['auth/signin']).fullPath));
      }),
    );

  // A merchant who switched store in the Shopify admin arrives here holding the *previous*
  // store's Goodchart token. It is still signature-valid, so every request would keep serving
  // that store's data — the iframe origin, and therefore localStorage, is shared by every store
  // the app is installed on. Drop it before reading it, so the cold-start branch below mints a
  // fresh one from *this* store's session token.
  if (authService.shopMismatch) {
    // Only a mismatch found mid-session can leave another store's data in the root singletons
    // (ChartService, PlanService, ...): a switch made through the Shopify admin is a top-level
    // navigation, so the app has just re-bootstrapped and they are already empty. Reload only in
    // the former case. This cannot loop — the reload happens after the offending token is gone,
    // so the next boot has nothing to mismatch against.
    const wasWarm = !!authService.user;

    authService.signOut();

    if (wasWarm) {
      pageReload.reload();

      return of(false);
    }
  }

  const token = localStorage.getItem(constants.AUTH_TOKEN_KEY as string);

  // A fresh Shopify embedded deep-link (e.g. a Sidekick intent landing) can have no
  // stored token at all. Unlike an *invalid* token, a *missing* one 401s without the
  // x-shopify-retry-invalid-session-request header, so shopifyInterceptor never gets a
  // chance to retry — the request below would fail outright and (since that failure is
  // a navigation) silently close the intent modal. Mint the access token from the Shopify
  // session up front so the very first authed call on a cold load succeeds.
  if (!token && window.shopify)
    return authService.exchangeShopifyToken().pipe(
      switchMap(() => validate()),
      catchError((error) => {
        captureException(error);

        return of(router.createUrlTree(RoutePath.getPath(Path['auth/error']).fullPath));
      }),
    );

  if ((!authService.user && token) || !token) return validate();

  if (authService.user) return true;

  if (window.shopify) return of(router.createUrlTree(RoutePath.getPath(Path['auth/error']).fullPath));

  return of(router.createUrlTree(RoutePath.getPath(Path['auth/signin']).fullPath));
};
